import { useState, useEffect, useCallback } from 'react';
import { Flex, Button } from '@chakra-ui/react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';

import {
  getStringFromDate,
  getDateFromString,
  DateInputWithConfirm,
  TextareaWithConfirm,
  Modal,
  RadioOptionsWithConfirm,
  IQuestion,
  CurrencyInputWithConfirm
} from 'shared-components';

import { ApplicationState } from '../../../../store';
import * as FinanceActions from '../../../../store/student/finanacials/actions';
import { Q } from '../../../../store/student/types';
import { ConfigField, IDate } from '../../../../types';
import { getMomentDate, isValidDate } from '../../../../utilities/validation';
import { formatCurrency } from '../../../../utilities/formatting';
import { getImportOffer } from '../../../../utilities/helpers';
import { getPageStatus } from './utils';

interface IProps {
  forceShow: boolean;
  onSave: () => void;
  onClose: () => void;
}

function DepositModal(props: IProps) {
  const { forceShow, onSave, onClose } = props;

  const dispatch = useDispatch();

  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any) =>
      dispatch(FinanceActions.createUpdateFinanceFieldAction(fieldName, value, refValue)),
    [dispatch]
  );

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const config = useSelector(({ config }: ApplicationState) => config, shallowEqual);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importCourseFeePaidToDate = importOffer?.courseFeePaidToDate || 0;
  const importAccomFeePaidToDate = importOffer?.accomFeePaidToDate || 0;

  const {
    DepositPaid: courseFeeDepositReqdConfig,
    DepositAmount: courseFeeDepositConfig,
    DepositDate: courseFeeDepositDateConfig,
    DepositReason: courseFeeDepositReasonConfig,
    AccommodationDepositPaid: accomDepositReqdConfig,
    AccommodationDepositAmount: accomDepositConfig,
    AccommodationDepositDate: accomDepositDateConfig
  }: {
    [key: string]: ConfigField;
  } = config.Checklist.Finances.Page1;

  const {
    courseFeeDepositReqd: prevCourseFeeDepositReqd = courseFeeDepositReqdConfig?.defaultValue,
    courseFeeDeposit: prevCourseFeeDeposit = courseFeeDepositConfig?.defaultValue,
    courseFeeDepositDate: prevCourseFeeDepositDate = courseFeeDepositDateConfig?.defaultValue,
    courseFeeDepositNotReqdReason: prevCourseFeeDepositReason = courseFeeDepositReasonConfig?.defaultValue,
    accomDepositReqd: prevAccomDepositReqd = accomDepositReqdConfig?.defaultValue,
    accomDeposit: prevAccomDeposit = accomDepositConfig?.defaultValue,
    accomDepositDate: prevAccomDepositDate = accomDepositDateConfig?.defaultValue
  } = student.financials;

  const [courseFeeDepositReqd, setCourseFeeDepositReqd] = useState(prevCourseFeeDepositReqd);
  const [courseFeeDeposit, setCourseFeeDeposit] = useState(prevCourseFeeDeposit);
  const [courseFeeDepositDate, setCourseFeeDepositDate] = useState(prevCourseFeeDepositDate);
  const [courseFeeDepositReason, setCourseFeeDepositReason] = useState(prevCourseFeeDepositReason);
  const [accomDepositReqd, setAccomDepositReqd] = useState(prevAccomDepositReqd);
  const [accomDeposit, setAccomDeposit] = useState(prevAccomDeposit);
  const [accomDepositDate, setAccomDepositDate] = useState(prevAccomDepositDate);

  const { complete, visible } = getPageStatus(config, {
    courseFeeDepositReqd,
    courseFeeDeposit,
    courseFeeDepositDate,
    courseFeeDepositNotReqdReason: courseFeeDepositReason,
    accomDepositReqd,
    accomDeposit,
    accomDepositDate
  });

  const [courseFeeDDate, setCourseFeeDDate] = useState(() => getDateFromString(courseFeeDepositDate));
  const [accomDDate, setAccomDDate] = useState(() => getDateFromString(accomDepositDate));

  const [courseFeeDepositDateError, setCourseFeeDepositDateError] = useState('');
  const [accomDepositDateError, setAccomDepositDateError] = useState('');

  const [importApproved, setImportApproved] = useState<string[]>(() => {
    const arr = [];
    if (isImportEnabled(Q.COURSE_FEE_DEPOSIT_REQD) && prevCourseFeeDepositReqd === 'Yes') {
      arr.push(Q.COURSE_FEE_DEPOSIT_REQD);
    }
    if (isImportEnabled(Q.COURSE_FEE_DEPOSIT) && prevCourseFeeDeposit === importCourseFeePaidToDate) {
      arr.push(Q.COURSE_FEE_DEPOSIT);
    }
    if (isImportEnabled(Q.ACCOMMODATION_DEPOSIT_REQD) && prevAccomDepositReqd === 'Yes') {
      arr.push(Q.ACCOMMODATION_DEPOSIT_REQD);
    }
    if (isImportEnabled(Q.ACCOMMODATION_DEPOSIT) && prevAccomDeposit === importAccomFeePaidToDate) {
      arr.push(Q.ACCOMMODATION_DEPOSIT);
    }
    return arr;
  });
  const [importEdited, setImportEdited] = useState<string[]>([]);
  const [importUpdateEnabled, setImportUpdateEnabled] = useState<string[]>(() => {
    const arr = [];
    if (
      isImportEnabled(Q.COURSE_FEE_DEPOSIT_REQD) &&
      !!prevCourseFeeDepositReqd &&
      prevCourseFeeDepositReqd !== 'Yes'
    ) {
      arr.push(Q.COURSE_FEE_DEPOSIT_REQD);
    }
    if (
      isImportEnabled(Q.COURSE_FEE_DEPOSIT) &&
      !!prevCourseFeeDeposit &&
      prevCourseFeeDeposit !== importCourseFeePaidToDate
    ) {
      arr.push(Q.COURSE_FEE_DEPOSIT);
    }
    if (isImportEnabled(Q.ACCOMMODATION_DEPOSIT_REQD) && !!prevAccomDepositReqd && prevAccomDepositReqd !== 'Yes') {
      arr.push(Q.ACCOMMODATION_DEPOSIT_REQD);
    }
    if (
      isImportEnabled(Q.ACCOMMODATION_DEPOSIT) &&
      !!prevAccomDeposit &&
      prevAccomDeposit !== importAccomFeePaidToDate
    ) {
      arr.push(Q.ACCOMMODATION_DEPOSIT);
    }
    return arr;
  });

  // handle side effects when courseFeeDepositReqd is changed
  useEffect(() => {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    if (courseFeeDepositReqd !== 'Yes') {
      handleImportUndo(Q.COURSE_FEE_DEPOSIT);
      setImportUpdateEnabled(importUpdateEnabled.filter(q => q !== Q.COURSE_FEE_DEPOSIT));
      setCourseFeeDeposit(0);
    }
    if (courseFeeDepositReqd !== 'No') {
      setCourseFeeDepositDate('');
      setCourseFeeDepositDateError('');
      setCourseFeeDDate({ DD: null, MM: null, YYYY: null });
    }
    if (courseFeeDepositReqd !== 'No - Not Required') {
      setCourseFeeDepositReason('');
    }
  }, [courseFeeDepositReqd]); // eslint-disable-line react-hooks/exhaustive-deps

  // handle side effects when accomDepositReqd is changed
  useEffect(() => {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    if (accomDepositReqd !== 'Yes') {
      handleImportUndo(Q.ACCOMMODATION_DEPOSIT);
      setImportUpdateEnabled(importUpdateEnabled.filter(q => q !== Q.ACCOMMODATION_DEPOSIT));
      setAccomDeposit(0);
    }
    if (accomDepositReqd !== 'Not yet - I plan on paying one') {
      setAccomDepositDate('');
      setAccomDepositDateError('');
      setAccomDDate({ DD: null, MM: null, YYYY: null });
    }
  }, [accomDepositReqd]); // eslint-disable-line react-hooks/exhaustive-deps

  function isImportEnabled(target: string): boolean {
    switch (target) {
      case Q.COURSE_FEE_DEPOSIT_REQD:
        return visible.courseFeeDepositReqd && !!importCourseFeePaidToDate;
      case Q.COURSE_FEE_DEPOSIT:
        return (
          courseFeeDepositConfig?.visible &&
          isImportEnabled(Q.COURSE_FEE_DEPOSIT_REQD) &&
          (!courseFeeDepositReqd || courseFeeDepositReqd === 'Yes')
        );
      case Q.ACCOMMODATION_DEPOSIT_REQD:
        return visible.accomDepositReqd && !!importAccomFeePaidToDate;
      case Q.ACCOMMODATION_DEPOSIT:
        return (
          accomDepositConfig?.visible &&
          isImportEnabled(Q.ACCOMMODATION_DEPOSIT_REQD) &&
          (!accomDepositReqd || accomDepositReqd === 'Yes')
        );
      default:
        return false;
    }
  }

  function isImportComplete(target: string) {
    if (!isImportEnabled(target) || importUpdateEnabled.includes(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  }

  function handleImportApprove(target: string) {
    setImportApproved([...importApproved, target]);
    setImportEdited(importEdited.filter(q => q !== target));
  }

  function handleImportEdit(target: string) {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  }

  function handleImportUndo(target: string) {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited(importEdited.filter(q => q !== target));
  }

  function handleCourseFeeDepositClick(question: IQuestion) {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    setCourseFeeDepositReqd(question.label === 'Not yet' ? 'No' : question.label);
  }

  function getComplete() {
    const importQuestions = [
      Q.COURSE_FEE_DEPOSIT_REQD,
      Q.COURSE_FEE_DEPOSIT,
      Q.ACCOMMODATION_DEPOSIT_REQD,
      Q.ACCOMMODATION_DEPOSIT
    ];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  }

  const handleAccomDepositClick = (question: IQuestion) => {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    setAccomDepositReqd(question.label);
  };

  const isValidFutureDate = (date: IDate, daysInFuture?: number): { isValid: boolean; message?: string } => {
    const { YYYY, DD, MM } = date;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(date);
      if (!isValid) {
        return { isValid: false, message: "Doesn't look like a valid date" };
      } else {
        const momentDate = getMomentDate(date);
        const days = moment().startOf('day').diff(momentDate, 'days');
        if (days > 0) {
          return {
            isValid: false,
            message: 'Date must be in the future'
          };
        } else if (days < (daysInFuture ? 0 - daysInFuture : -365)) {
          return {
            isValid: false,
            message: 'Date must be within 12 months'
          };
        } else {
          return { isValid: true };
        }
      }
    } else {
      return { isValid: false, message: '' };
    }
  };

  const handleCourseFeeDepositDateChange = (date?: IDate) => {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    const feeDate = date ?? courseFeeDDate;
    const { isValid, message } = isValidFutureDate(feeDate);
    if (isValid) {
      setCourseFeeDepositDate(getStringFromDate(feeDate));
      setCourseFeeDepositDateError('');
    } else {
      setCourseFeeDepositDate('');
      if (message) {
        setCourseFeeDepositDateError(message);
      }
    }
  };

  const handleAccomDepositDateChange = (date?: IDate) => {
    if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289

    const accomDate = date ?? accomDDate;
    const { isValid, message } = isValidFutureDate(accomDate);
    if (isValid) {
      setAccomDepositDate(getStringFromDate(accomDate));
      setAccomDepositDateError('');
    } else {
      setAccomDepositDate('');
      if (accomDepositDateError === '' && message) {
        setAccomDepositDateError(message);
      }
    }
  };

  const handleSaveClick = () => {
    onChange(Q.COURSE_FEE_DEPOSIT_REQD, courseFeeDepositReqd);
    onChange(Q.COURSE_FEE_DEPOSIT, courseFeeDeposit);
    onChange(Q.COURSE_FEE_DEPOSIT_DATE, courseFeeDepositDate);
    onChange(Q.COURSE_FEE_DEPOSIT_NOT_REQD_REASON, courseFeeDepositReason);

    onChange(Q.ACCOMMODATION_DEPOSIT_REQD, accomDepositReqd);
    onChange(Q.ACCOMMODATION_DEPOSIT, accomDeposit);
    onChange(Q.ACCOMMODATION_DEPOSIT_DATE, accomDepositDate);

    onSave();
    onClose();
    return true;
  };

  return (
    <Modal
      id="deposit"
      title="Add/Update Deposit"
      show={true}
      canClose={!forceShow}
      onClose={onClose}
      hideFooter={true}
    >
      {visible.courseFeeDepositReqd && (
        <RadioOptionsWithConfirm
          key={Q.COURSE_FEE_DEPOSIT_REQD}
          id={Q.COURSE_FEE_DEPOSIT_REQD}
          value={courseFeeDepositReqd}
          label={courseFeeDepositReqdConfig?.label}
          inputHelperText={courseFeeDepositReqdConfig?.helpText}
          onClick={(question: IQuestion) => handleCourseFeeDepositClick(question)}
          questions={[
            {
              char: '1',
              label: 'Yes',
              selected: courseFeeDepositReqd === 'Yes'
            },
            {
              char: '2',
              label: 'Not yet',
              selected: courseFeeDepositReqd === 'No'
            },
            {
              char: '3',
              label: 'No - I have a full scholarship',
              selected: courseFeeDepositReqd === 'No - I have a full scholarship'
            },
            {
              char: '4',
              label: 'No - Not Required',
              selected: courseFeeDepositReqd === 'No - Not Required'
            }
          ]}
          importEnabled={isImportEnabled(Q.COURSE_FEE_DEPOSIT_REQD)}
          importValue={'Yes'}
          importUpdateEnabled={importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT_REQD)}
          importApproved={importApproved.includes(Q.COURSE_FEE_DEPOSIT_REQD)}
          importEdited={importEdited.includes(Q.COURSE_FEE_DEPOSIT_REQD)}
          onImportApprove={() => {
            handleImportApprove(Q.COURSE_FEE_DEPOSIT_REQD);
            setCourseFeeDepositReqd('Yes');
          }}
          onImportEdit={() => {
            handleImportEdit(Q.COURSE_FEE_DEPOSIT_REQD);
            setCourseFeeDepositReqd(
              importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT_REQD) ? courseFeeDepositReqd : 'Yes'
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.COURSE_FEE_DEPOSIT_REQD);
            setCourseFeeDepositReqd(
              importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT_REQD) ? prevCourseFeeDepositReqd : ''
            );
          }}
          hideEditButton={!!config.depositUneditable}
        />
      )}

      {(visible.courseFeeDeposit || isImportEnabled(Q.COURSE_FEE_DEPOSIT)) && (
        <CurrencyInputWithConfirm
          key={Q.COURSE_FEE_DEPOSIT}
          id={Q.COURSE_FEE_DEPOSIT}
          value={courseFeeDeposit}
          label={courseFeeDepositConfig?.label}
          inputHelperText={courseFeeDepositConfig?.helpText}
          onChange={(evt: any) => {
            if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289
            setCourseFeeDeposit(Number(evt.currentTarget.value) || 0);
          }}
          importEnabled={isImportEnabled(Q.COURSE_FEE_DEPOSIT)}
          importValue={`£${formatCurrency(importCourseFeePaidToDate ?? 0)}`}
          importUpdateEnabled={importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT)}
          importApproved={importApproved.includes(Q.COURSE_FEE_DEPOSIT)}
          importEdited={importEdited.includes(Q.COURSE_FEE_DEPOSIT)}
          onImportApprove={() => {
            handleImportApprove(Q.COURSE_FEE_DEPOSIT);
            setCourseFeeDeposit(importCourseFeePaidToDate);
          }}
          onImportEdit={() => {
            if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289
            handleImportEdit(Q.COURSE_FEE_DEPOSIT);
            setCourseFeeDeposit(
              importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT) ? courseFeeDeposit : importCourseFeePaidToDate
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.COURSE_FEE_DEPOSIT);
            setCourseFeeDeposit(importUpdateEnabled.includes(Q.COURSE_FEE_DEPOSIT) ? prevCourseFeeDeposit : 0);
          }}
          hideEditButton={!!config.depositUneditable}
        />
      )}

      {visible.courseFeeDepositDate && (
        <DateInputWithConfirm
          key={Q.COURSE_FEE_DEPOSIT_DATE}
          id={Q.COURSE_FEE_DEPOSIT_DATE}
          label={courseFeeDepositDateConfig?.label}
          value={courseFeeDDate}
          autoFocus={true}
          inputHelperText={courseFeeDepositDateConfig?.helpText}
          errorText={courseFeeDepositDateError}
          onChangeDD={(evt: any) =>
            setCourseFeeDDate({
              ...courseFeeDDate,
              DD: evt.target.value
            })
          }
          onChangeMM={(evt: any) =>
            setCourseFeeDDate({
              ...courseFeeDDate,
              MM: evt.target.value
            })
          }
          onChangeYY={(evt: any) => {
            if (evt.target.value.length === 4) {
              setCourseFeeDDate({
                ...courseFeeDDate,
                YYYY: evt.target.value
              });
              handleCourseFeeDepositDateChange({
                ...courseFeeDDate,
                YYYY: evt.target.value
              });
            } else {
              setCourseFeeDDate({
                ...courseFeeDDate,
                YYYY: evt.target.value
              });
            }
          }}
          onBlur={() => handleCourseFeeDepositDateChange()}
        />
      )}

      {visible.courseFeeDepositNotReqdReason && (
        <TextareaWithConfirm
          key={Q.COURSE_FEE_DEPOSIT_NOT_REQD_REASON}
          id={Q.COURSE_FEE_DEPOSIT_NOT_REQD_REASON}
          value={courseFeeDepositReason}
          label={courseFeeDepositReasonConfig?.label}
          inputHelperText={courseFeeDepositReasonConfig?.helpText}
          onChange={(evt: any) => setCourseFeeDepositReason(evt.currentTarget.value)}
        />
      )}

      {visible.accomDepositReqd && (
        <RadioOptionsWithConfirm
          key={Q.ACCOMMODATION_DEPOSIT_REQD}
          id={Q.ACCOMMODATION_DEPOSIT_REQD}
          value={accomDepositReqd}
          label={accomDepositReqdConfig?.label}
          inputHelperText={accomDepositReqdConfig?.helpText}
          onClick={(question: IQuestion) => handleAccomDepositClick(question)}
          questions={[
            {
              char: '1',
              label: 'Yes',
              selected: accomDepositReqd === 'Yes'
            },
            {
              char: '2',
              label: 'Not yet - I plan on paying one',
              selected: accomDepositReqd === 'Not yet - I plan on paying one'
            },
            {
              char: '3',
              label: 'No - Not Required',
              selected: accomDepositReqd === 'No - Not Required'
            }
          ]}
          importEnabled={isImportEnabled(Q.ACCOMMODATION_DEPOSIT_REQD)}
          importValue={'Yes'}
          importUpdateEnabled={importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT_REQD)}
          importApproved={importApproved.includes(Q.ACCOMMODATION_DEPOSIT_REQD)}
          importEdited={importEdited.includes(Q.ACCOMMODATION_DEPOSIT_REQD)}
          onImportApprove={() => {
            handleImportApprove(Q.ACCOMMODATION_DEPOSIT_REQD);
            setAccomDepositReqd('Yes');
          }}
          onImportEdit={() => {
            if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289
            handleImportEdit(Q.ACCOMMODATION_DEPOSIT_REQD);
            setAccomDepositReqd(importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT_REQD) ? accomDepositReqd : 'Yes');
          }}
          onImportUndo={() => {
            handleImportUndo(Q.ACCOMMODATION_DEPOSIT_REQD);
            setAccomDepositReqd(importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT_REQD) ? prevAccomDepositReqd : '');
          }}
          hideEditButton={!!config.depositUneditable}
        />
      )}

      {(visible.accomDeposit || isImportEnabled(Q.ACCOMMODATION_DEPOSIT)) && (
        <CurrencyInputWithConfirm
          key={Q.ACCOMMODATION_DEPOSIT}
          id={Q.ACCOMMODATION_DEPOSIT}
          value={accomDeposit}
          label={accomDepositConfig?.label}
          inputHelperText={accomDepositConfig?.helpText}
          onChange={(evt: any) => setAccomDeposit(Number(evt.currentTarget.value) || 0)}
          importEnabled={isImportEnabled(Q.ACCOMMODATION_DEPOSIT)}
          importValue={`£${formatCurrency(importAccomFeePaidToDate ?? 0)}`}
          importUpdateEnabled={importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT)}
          importApproved={importApproved.includes(Q.ACCOMMODATION_DEPOSIT)}
          importEdited={importEdited.includes(Q.ACCOMMODATION_DEPOSIT)}
          onImportApprove={() => {
            handleImportApprove(Q.ACCOMMODATION_DEPOSIT);
            setAccomDeposit(importAccomFeePaidToDate);
          }}
          onImportEdit={() => {
            if (config.depositUneditable) return; // Hack to make all deposite fields uneditable for STM-289
            handleImportEdit(Q.ACCOMMODATION_DEPOSIT);
            setAccomDeposit(
              importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT) ? accomDeposit : importAccomFeePaidToDate
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.ACCOMMODATION_DEPOSIT);
            setAccomDeposit(importUpdateEnabled.includes(Q.ACCOMMODATION_DEPOSIT) ? prevAccomDeposit : 0);
          }}
          hideEditButton={!!config.depositUneditable}
        />
      )}

      {visible.accomDepositDate && (
        <DateInputWithConfirm
          key={Q.ACCOMMODATION_DEPOSIT_DATE}
          id={Q.ACCOMMODATION_DEPOSIT_DATE}
          label={accomDepositDateConfig?.label}
          value={accomDDate}
          autoFocus={true}
          inputHelperText={accomDepositDateConfig?.helpText}
          errorText={accomDepositDateError}
          onChangeDD={(evt: any) => setAccomDDate({ ...accomDDate, DD: evt.target.value })}
          onChangeMM={(evt: any) => setAccomDDate({ ...accomDDate, MM: evt.target.value })}
          onChangeYY={(evt: any) => {
            if (evt.target.value.length === 4) {
              setAccomDDate({
                ...accomDDate,
                YYYY: evt.target.value
              });
              handleAccomDepositDateChange({
                ...accomDDate,
                YYYY: evt.target.value
              });
            } else {
              setAccomDDate({
                ...accomDDate,
                YYYY: evt.target.value
              });
            }
          }}
          onBlur={() => handleAccomDepositDateChange()}
        />
      )}

      <Flex justifyContent="end">
        <Button id={'saveButton'} isDisabled={!getComplete()} onClick={handleSaveClick} size="lg" variant="solid">
          Save Deposit Details
        </Button>
      </Flex>
    </Modal>
  );
}

export default DepositModal;
